// User actions on friends / trips / candidates. These mutate the Svelte stores;
// markers follow reactively via the marker-manager. Side effects (recompute,
// suggestion refresh, reverse-geocode) are triggered here, not in components.

import { get } from 'svelte/store';
import {
  friends, trips, candidates, activeCandidateId,
  friendsRaw, tripsRaw, genId, showToast,
  updateFriend, updateTrip, updateCandidate,
} from './state';
import { geocode, reverseGeocode } from './api';
import { CANDIDATE_COLORS } from './scoring';
import { defaultNameFromAddress, nameFromPrediction, parseListingUrl } from './address';
import {
  enrichCandidate, computeAllModesForCandidate, computeTripsForCandidate,
  recomputeCandidatesForFriendChange, recomputeCandidatesForTripChange,
} from './compute';
import { clearSuggestions } from './suggestions';
import { requestName } from './namePrompt';
import type { Candidate, Friend, LatLng, Prediction, Suggestion, Trip } from './types';

/**
 * How the new pin gets its name. Omitted entirely (the desktop sidebar and the
 * legacy drop-pin flows) means "open the name prompt". `skipPrompt` means the
 * caller already asked — the mobile add sheet collects the name with the
 * address — so an empty `name` just takes the derived default.
 */
export interface AddOptions {
  name?: string;
  skipPrompt?: boolean;
}

async function pickName(
  opts: AddOptions | undefined,
  request: { title: string; label: string; defaultValue: string },
): Promise<string | null> {
  if (opts?.skipPrompt) return opts.name?.trim() || request.defaultValue;
  return requestName(request);
}

// ---- textarea projections ----
function rebuildFriendsRaw(): void {
  friendsRaw.set(get(friends).map((f) => `${f.name} | ${f.address}`).join('\n'));
}
function rebuildTripsRaw(): void {
  tripsRaw.set(get(trips).map((t) => `${t.name} | ${t.address}`).join('\n'));
}

// ============ Friends ============
export function addFriend(f: Omit<Friend, 'id'>): Friend {
  const friend: Friend = { id: genId('f'), ...f };
  friends.update((list) => [...list, friend]);
  rebuildFriendsRaw();
  clearSuggestions();
  recomputeCandidatesForFriendChange();
  return friend;
}

export async function addFriendByAddress(
  value: string,
  prediction?: Prediction | null,
  opts?: AddOptions,
): Promise<boolean> {
  const r = await geocode(value);
  const suggestedName = nameFromPrediction(prediction ?? null, r.formattedAddress);
  const name = await pickName(opts, { title: 'Who lives here?', label: 'Name(s)', defaultValue: suggestedName });
  if (!name) return false;
  addFriend({
    name,
    address: r.formattedAddress,
    lat: r.lat,
    lng: r.lng,
    formattedAddress: r.formattedAddress,
  });
  showToast(`Added ${name} ✓`);
  return true;
}

export async function addFriendAtLocation(latLng: LatLng, opts?: AddOptions): Promise<boolean> {
  const address = await reverseGeocode(latLng.lat, latLng.lng);
  const name = await pickName(opts, { title: 'Who lives here?', label: 'Name(s)', defaultValue: defaultNameFromAddress(address) });
  if (!name) return false;
  addFriend({ name, address, lat: latLng.lat, lng: latLng.lng, formattedAddress: address });
  showToast(`Added ${name} ✓`);
  return true;
}

export function removeFriend(id: string): void {
  friends.update((list) => list.filter((f) => f.id !== id));
  rebuildFriendsRaw();
  clearSuggestions();
  recomputeCandidatesForFriendChange();
}

export function renameFriend(id: string, name: string): void {
  updateFriend(id, { name });
  rebuildFriendsRaw();
}

export async function friendMoved(id: string, latLng: google.maps.LatLng): Promise<void> {
  const lat = latLng.lat();
  const lng = latLng.lng();
  updateFriend(id, { lat, lng });
  const address = await reverseGeocode(lat, lng);
  updateFriend(id, { address, formattedAddress: address });
  rebuildFriendsRaw();
  clearSuggestions();
  await recomputeCandidatesForFriendChange();
}

// ============ Trips (favorite places) ============
export function addTrip(t: Omit<Trip, 'id'>): Trip {
  const trip: Trip = { id: genId('t'), ...t };
  trips.update((list) => [...list, trip]);
  rebuildTripsRaw();
  clearSuggestions();
  recomputeCandidatesForTripChange();
  return trip;
}

export async function addTripByAddress(
  value: string,
  prediction?: Prediction | null,
  opts?: AddOptions,
): Promise<boolean> {
  // The input may display only the name; resolve the complete selected result.
  const r = await geocode(prediction?.description || value);
  const suggestedName = nameFromPrediction(prediction ?? null, r.formattedAddress);
  const name = await pickName(opts, { title: 'Name this place', label: 'Place name', defaultValue: suggestedName });
  if (!name) return false;
  addTrip({
    name,
    address: r.formattedAddress,
    lat: r.lat,
    lng: r.lng,
    formattedAddress: r.formattedAddress,
  });
  showToast(`Added ${name} ✓`);
  return true;
}

export async function addTripAtLocation(latLng: LatLng, opts?: AddOptions): Promise<boolean> {
  const address = await reverseGeocode(latLng.lat, latLng.lng);
  const name = await pickName(opts, { title: 'Name this place', label: 'Place name', defaultValue: defaultNameFromAddress(address) });
  if (!name) return false;
  addTrip({ name, address, lat: latLng.lat, lng: latLng.lng, formattedAddress: address });
  showToast(`Added ${name} ✓`);
  return true;
}

export function removeTrip(id: string): void {
  trips.update((list) => list.filter((t) => t.id !== id));
  rebuildTripsRaw();
  clearSuggestions();
  recomputeCandidatesForTripChange();
}

export function renameTrip(id: string, name: string): void {
  updateTrip(id, { name });
  rebuildTripsRaw();
}

export async function tripMoved(id: string, latLng: google.maps.LatLng): Promise<void> {
  const lat = latLng.lat();
  const lng = latLng.lng();
  updateTrip(id, { lat, lng });
  const address = await reverseGeocode(lat, lng);
  updateTrip(id, { address, formattedAddress: address });
  rebuildTripsRaw();
  clearSuggestions();
  await recomputeCandidatesForTripChange();
}

// ============ Candidates ============
export function setActiveCandidate(id: string): void {
  activeCandidateId.set(id);
}

export function addCandidate(input: { name: string; address: string; lat: number; lng: number }): Candidate {
  const color = CANDIDATE_COLORS[get(candidates).length % CANDIDATE_COLORS.length];
  const candidate: Candidate = {
    id: genId('c'),
    name: input.name,
    address: input.address,
    lat: input.lat,
    lng: input.lng,
    color,
    results: {},
    amenities: null,
    trips: {},
  };
  candidates.update((list) => [...list, candidate]);
  setActiveCandidate(candidate.id);
  clearSuggestions();
  enrichCandidate(candidate.id);
  return candidate;
}

export async function addCandidateByAddress(
  rawAddress: string,
  prediction?: Prediction | null,
  opts?: AddOptions,
): Promise<boolean> {
  const parsed = parseListingUrl(rawAddress);
  const address = parsed?.address || prediction?.description || rawAddress;
  const r = await geocode(address);
  const suggestedName = parsed?.streetAddress || nameFromPrediction(prediction ?? null, r.formattedAddress);
  const name = await pickName(opts, { title: 'Name this home', label: 'Home name', defaultValue: suggestedName });
  if (!name) return false;
  addCandidate({
    name,
    address: r.formattedAddress,
    lat: r.lat,
    lng: r.lng,
  });
  showToast(`Added ${name} ✓`);
  return true;
}

export async function addCandidateAtLocation(latLng: LatLng, opts?: AddOptions): Promise<boolean> {
  const address = await reverseGeocode(latLng.lat, latLng.lng);
  const suggestedName = defaultNameFromAddress(address) || `Home ${get(candidates).length + 1}`;
  const name = await pickName(opts, { title: 'Name this home', label: 'Home name', defaultValue: suggestedName });
  if (!name) return false;
  addCandidate({ name, address, lat: latLng.lat, lng: latLng.lng });
  showToast(`Added ${name} ✓`);
  return true;
}

export function removeCandidate(id: string): void {
  const list = get(candidates);
  const remaining = list.filter((c) => c.id !== id);
  candidates.set(remaining);
  if (get(activeCandidateId) === id) {
    activeCandidateId.set(remaining[0]?.id ?? null);
  }
  clearSuggestions();
}

export function renameCandidate(id: string, name: string): void {
  updateCandidate(id, { name });
}

export async function candidateMoved(id: string, latLng: google.maps.LatLng): Promise<void> {
  const lat = latLng.lat();
  const lng = latLng.lng();
  // Wipe stale results so the UI shows "computing…" while we refetch.
  updateCandidate(id, {
    lat, lng,
    results: {}, amenities: null, trips: {},
    address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
  });
  clearSuggestions();
  const address = await reverseGeocode(lat, lng);
  updateCandidate(id, { address });
  enrichCandidate(id);
}

export async function promoteSuggestion(sug: Pick<Suggestion, 'lat' | 'lng' | 'rank' | 'neighborhoodName'>): Promise<void> {
  const fallbackName = sug.neighborhoodName ? `${sug.neighborhoodName} candidate` : `Top pick #${sug.rank}`;
  const name = prompt('Name this candidate', fallbackName);
  if (!name) return;
  const address = await reverseGeocode(sug.lat, sug.lng);
  addCandidate({ name, address, lat: sug.lat, lng: sug.lng });
}

// ============ "Edit as text" re-geocode ============
export async function geocodeFriendsFromText(): Promise<void> {
  const lines = get(friendsRaw).split('\n').map((l) => l.trim()).filter(Boolean);
  const next: Friend[] = [];
  for (const line of lines) {
    const [name, address] = line.split('|').map((s) => s.trim());
    if (!name || !address) continue;
    try {
      const r = await geocode(address);
      next.push({ id: genId('f'), name, address: r.formattedAddress, lat: r.lat, lng: r.lng, formattedAddress: r.formattedAddress });
    } catch {
      /* skip ungeocodable line */
    }
  }
  friends.set(next);
  rebuildFriendsRaw();
  clearSuggestions();
  await recomputeCandidatesForFriendChange();
}

export async function geocodeTripsFromText(): Promise<void> {
  const lines = get(tripsRaw).split('\n').map((l) => l.trim()).filter(Boolean);
  const next: Trip[] = [];
  for (const line of lines) {
    const [name, address] = line.split('|').map((s) => s.trim());
    if (!name || !address) continue;
    try {
      const r = await geocode(address);
      next.push({ id: genId('t'), name, address: r.formattedAddress, lat: r.lat, lng: r.lng, formattedAddress: r.formattedAddress });
    } catch {
      /* skip */
    }
  }
  trips.set(next);
  rebuildTripsRaw();
  clearSuggestions();
  await recomputeCandidatesForTripChange();
}
